import { Box, Typography } from '@mui/material';
import { FormTitleBox, LoadingSkeleton, ConfirmationDialog, PaginationTable } from '../../../common/components';
import LotsTableView from './components/LotsTableView';
import LotDialog from './components/LotDialog';
import LotsPlanView from './components/LotsPlanView';
import LotsHeaderControls from './components/LotsHeaderControls';
import { useLotsTable } from './hooks/useLotsTable';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useMemo, useState } from 'react';
import usePermissions from '../../../hooks/usePermissions';

const LotsTable = () => {
  const [view, setView] = useState('plan');
  const { permissions } = usePermissions();
  const canEditLots = Boolean(permissions?.canEditLots);
  const {
    allotments,
    allLots,
    projects,
    contracts,
    selectedAllotment,
    handleSelectAllotment,
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedLot,
    selectedProject,
    selectedContract,
    subscribers,
    deleteConfirmOpen,
    lotToDelete,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleSave,
    handleEdit,
    handleUpdateLot,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  } = useLotsTable();

  const selectedAllotmentData = useMemo(
    () => allotments.find((item) => item.uuid === selectedAllotment),
    [allotments, selectedAllotment]
  );

  const projectsMap = useMemo(() => {
    return projects.reduce((acc, project) => {
      if (project.uuid_lot) {
        acc[project.uuid_lot] = project;
      }
      return acc;
    }, {});
  }, [projects]);

  const isTableView = view === 'table';

  return (
    <Box>
      <FormTitleBox
        title="Obra | Lotes | Lotes"
        showButton={isTableView}
        buttonText="Agregar Lote"
        buttonIcon={HomeWorkIcon}
        onButtonClick={handleOpenDialog}
        buttonProps={{ disabled: !canEditLots }}
        showSearch={isTableView}
        searchLabel="Buscar lote"
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchSize="medium"
      />

      <LotsHeaderControls
        allotments={allotments}
        selectedAllotment={selectedAllotment}
        onSelectAllotment={handleSelectAllotment}
        view={view}
        onViewChange={setView}
      />

      {selectedAllotment ? (
        view === 'plan' ? (
          <LotsPlanView allotment={selectedAllotmentData} canEditLots={canEditLots} />
        ) : (
          <Box>
            <LotsTableView
              data={data}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              canEditLots={canEditLots}
              LoadingComponent={LoadingSkeleton}
              projectsMap={projectsMap}
            />
            <PaginationTable
              totalPages={totalPages}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        )
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Seleccioná un loteo para ver los lotes.
          </Typography>
        </Box>
      )}

      <LotDialog
        open={dialogOpen && !editMode}
        onClose={handleCloseDialog}
        editMode={false}
        selectedLot={selectedLot}
        onSave={handleSave}
        canEditLots={canEditLots}
        contracts={contracts}
        allLots={allLots}
      />

      <LotDialog
        open={dialogOpen && editMode}
        onClose={handleCloseDialog}
        editMode={true}
        selectedLot={selectedLot}
        contracts={contracts}
        allLots={allLots}
        project={selectedProject}
        contract={selectedContract}
        subscribers={subscribers}
        onSave={handleUpdateLot}
        canEditLots={canEditLots}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Eliminar Lote"
        message={`¿Está seguro que desea eliminar el lote "${lotToDelete?.lot_number}"?`}
        confirmText="Eliminar lote"
        confirmDisabled={!canEditLots}
      />
    </Box>
  );
};

export default LotsTable;
